import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BarChart3, ChevronLeft, ChevronRight, Download, Filter, RefreshCw, UserRound } from 'lucide-react';
import { hojeNoFuso } from '../../componentes/periodo/periodo';
import { Botao, Cartao, classesBotao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { CampoData, Rotulo, Selecao } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { get, getTodos, mensagemDeErro } from '../../nucleo/api';
import { data, dataHora, dinheiro, mesAno, nomePessoa, numero, percentual, textoLivre } from '../../nucleo/formato';
import { rotuloEtapaPipeline, rotuloFunilPipeline } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { baixarTexto } from './CrmComponentes';
import { csv } from './crm-sugestoes';
import { caminhoDaNegociacao, caminhoDoRelatorio, funilDoPedido } from './crm-caminhos';
import { useFunis } from './Funis';

/**
 * Página "Relatórios" do CRM (lista de 02/11): Funil de hoje, Evolução mês a mês, Origem e Negócios parados, cada bloco com a
 * própria consulta (carregando, erro e vazio por bloco) e o próprio CSV. Funil (um por vez) e responsável na barra; o período
 * em meses vale na Evolução e na Origem. O relatório de sempre (`/crm/atividades/relatorio`) tem link no cabeçalho. Valores
 * pelos formatadores (modo discreto) e nunca somados entre moedas.
 */

export interface ValorMoedaRel {
  moeda: string;
  total_centavos: number;
  unico_centavos: number;
  recorrente_centavos: number;
}
export interface FunilHoje {
  funil_id: string;
  etapas: { etapa_id: string; nome: string; abertas: number; pausadas: number; valores: { moeda: string; total_centavos: number }[] }[];
}
export interface EvolucaoMes {
  mes: string;
  leads: number;
  vendas: number;
  perdas: number;
  taxa: number | null;
  valores: ValorMoedaRel[];
}
export interface Evolucao {
  funil_id: string;
  de: string;
  ate: string;
  fuso: string;
  meses: EvolucaoMes[];
  total: { leads: number; vendas: number; perdas: number; taxa: number | null; valores: ValorMoedaRel[] };
}
export interface Origem {
  funil_id: string;
  de: string;
  ate: string;
  fontes: { fonte: string | null; leads: number; vendas: number; valores: ValorMoedaRel[] }[];
  truncado: boolean;
}
export type MotivoParado = 'sem_proxima_tarefa' | 'tarefa_vencida' | 'sem_movimento';
export const MOTIVOS_PARADO: readonly MotivoParado[] = ['sem_proxima_tarefa', 'tarefa_vencida', 'sem_movimento'];
export interface Parado {
  id: string;
  titulo: string;
  parceiro_nome: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  etapa_id: string;
  etapa_nome: string;
  valor_centavos: number;
  moeda: string;
  motivos: MotivoParado[];
  ultimo_movimento: string;
  dias_sem_movimento: number;
  tarefa_vencida_em: string | null;
}
export interface Parados {
  funil_id: string;
  total: number;
  pagina: number;
  itens: Parado[];
}

/** Teto de meses do período (o servidor recusa acima: 422 `periodo.longo`). */
export const MAX_MESES = 24;
/** Tamanho da página da lista de parados e o teto de linhas do CSV (5 páginas de 200). */
export const LIMITE_PAGINA = 25;
export const LIMITE_CSV = 1000;

// De 1900 a 2099, como o esquema `Mes` do servidor (rl0211-1 ROB-B3).
const MES = /^(19|20)\d{2}-(0[1-9]|1[0-2])$/;
/** Quantos meses vão de `de` a `ate` (inclusive); 0 quando um deles não é AAAA-MM. */
export function mesesEntre(de: string, ate: string): number {
  if (!MES.test(de) || !MES.test(ate)) return 0;
  const n = (m: string) => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7));
  return n(ate) - n(de) + 1;
}
/**
 * Período escolhido: vazio = o padrão do servidor (12 meses até o atual); erro quando invertido ou acima do teto. Só o "De" vale
 * até o mês atual (como o servidor, que completa o que falta; o `mesAtual` vem do fuso do workspace), e só o "Até" vale 12 meses.
 */
export function validarPeriodo(de: string, ate: string, mesAtual?: string): 'invalido' | 'longo' | null {
  if (!de && !ate) return null;
  if ((de && !MES.test(de)) || (ate && !MES.test(ate))) return 'invalido';
  const fim = ate || mesAtual;
  if (de && fim && MES.test(fim)) {
    const n = mesesEntre(de, fim);
    if (n < 1) return 'invalido';
    if (n > MAX_MESES) return 'longo';
  }
  return null;
}

/** Valor por moeda (nunca somando moedas): cada moeda no formatador, separadas por " · "; sem linhas, "-". */
export function valoresTexto<T extends { moeda: string }>(linhas: T[], f: (l: T) => number): string {
  return linhas.length ? linhas.map((l) => dinheiro(f(l), l.moeda)).join(' · ') : '-';
}
/** Taxa de fechamento (fração de 0 a 1) em percentual; "-" quando nada fechou. */
export const taxaTexto = (taxa: number | null): string => (taxa === null ? '-' : `${percentual(taxa * 100, 1)}%`);

type Traduzir = (chave: string, o?: Record<string, unknown>) => string;

/** As linhas de cada CSV, só com os dados do servidor (valores e textos pelos formatadores: o modo discreto vale no arquivo). */
/** No CSV, sem dado a célula fica vazia (um "-" no começo viraria "'-" pela proteção contra fórmula do `csv`). */
const vazioNoCsv = (v: string) => (v === '-' ? '' : v);

export const BLOCOS_CSV_REL = {
  funil: (d: FunilHoje, t: Traduzir) => [
    [t('crm.relatorios.funilHoje.etapa'), t('crm.relatorios.funilHoje.abertas'), t('crm.relatorios.funilHoje.valor'), t('crm.relatorios.funilHoje.pausadas')],
    ...d.etapas.map((x) => [rotuloEtapaPipeline(x.nome), x.abertas, vazioNoCsv(valoresTexto(x.valores, (v) => v.total_centavos)), x.pausadas]),
  ],
  evolucao: (d: Evolucao, t: Traduzir) => [
    ['mes', 'leads', 'vendas', 'perdas', 'taxa', 'recorrente', 'pontual', 'total'].map((k) => t(`crm.relatorios.evolucao.${k}`)),
    ...d.meses.map((m) => [mesAno(m.mes), m.leads, m.vendas, m.perdas, vazioNoCsv(taxaTexto(m.taxa)), vazioNoCsv(valoresTexto(m.valores, (v) => v.recorrente_centavos)), vazioNoCsv(valoresTexto(m.valores, (v) => v.unico_centavos)), vazioNoCsv(valoresTexto(m.valores, (v) => v.total_centavos))]),
    [t('crm.relatorios.evolucao.totalPeriodo'), d.total.leads, d.total.vendas, d.total.perdas, vazioNoCsv(taxaTexto(d.total.taxa)), vazioNoCsv(valoresTexto(d.total.valores, (v) => v.recorrente_centavos)), vazioNoCsv(valoresTexto(d.total.valores, (v) => v.unico_centavos)), vazioNoCsv(valoresTexto(d.total.valores, (v) => v.total_centavos))],
  ],
  origem: (d: Origem, t: Traduzir) => [
    [t('crm.relatorios.origem.fonte'), t('crm.relatorios.origem.leads'), t('crm.relatorios.origem.vendas'), t('crm.relatorios.origem.valor')],
    ...d.fontes.map((x) => [x.fonte ? textoLivre(x.fonte) : t('crm.relatorios.origem.semFonte'), x.leads, x.vendas, vazioNoCsv(valoresTexto(x.valores, (v) => v.total_centavos))]),
  ],
  parados: (itens: Parado[], t: Traduzir) => [
    [t('crm.relatorios.parados.negociacao'), t('crm.relatorios.parados.etapa'), t('crm.relatorios.parados.responsavel'), t('crm.relatorios.parados.valor'), t('crm.relatorios.parados.motivo'), t('crm.relatorios.parados.ultimoMovimento')],
    ...itens.map((x) => [
      textoLivre(x.titulo, x.parceiro_nome),
      rotuloEtapaPipeline(x.etapa_nome),
      x.responsavel_nome ? nomePessoa(x.responsavel_nome) : '',
      dinheiro(x.valor_centavos, x.moeda),
      x.motivos.map((m) => t(`crm.relatorios.parados.motivos.${m}`)).join(' + '),
      dataHora(x.ultimo_movimento),
    ]),
  ],
} satisfies Record<string, (d: never, t: Traduzir) => (string | number)[][]>;
type Bloco = keyof typeof BLOCOS_CSV_REL;

interface Recorte {
  funil_id: string;
  responsaveis?: string;
}

export function PaginaRelatoriosCrm() {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const funis = useFunis();
  const ativos = (funis.data?.itens ?? []).filter((f) => !f.arquivado_em);
  const [funilId, setFunilId] = useState('');
  const funil = funilDoPedido(funilId, ativos);
  const [responsavel, setResponsavel] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const erroPeriodo = validarPeriodo(de, ate, hojeNoFuso(s.workspace.fuso).slice(0, 7));
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
  const outros = (membros.data?.itens ?? []).filter((m) => m.id !== s.usuario.id);
  const opcoesResp = [{ valor: s.usuario.id, rotulo: t('crm.relatorios.minhas') }, ...outros.map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))];
  const recorte = useMemo<Recorte>(() => ({ funil_id: funil, ...(responsavel ? { responsaveis: responsavel } : {}) }), [funil, responsavel]);
  // Período só vale válido; vazio = o padrão do servidor.
  const periodo = useMemo(() => (erroPeriodo ? {} : { ...(de ? { de } : {}), ...(ate ? { ate } : {}) }), [de, ate, erroPeriodo]);

  return (
    <div className="flex min-w-0 flex-col gap-4" data-pagina-relatorios-crm="">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('casca.relatorios')}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('crm.relatorios.titulo')}</h1>
          <p className="mt-1 text-sm text-tinta-3">{t('crm.relatorios.subtitulo')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link className={classesBotao('secundario')} to={caminhoDoRelatorio({ funil: funil || undefined, responsavel: responsavel || undefined })} data-ver-relatorio-completo="">
            <BarChart3 className="size-4" aria-hidden />
            {t('crm.relatorios.verCompleto')}
          </Link>
          <Botao
            icone={<RefreshCw className="size-4" />}
            data-recarregar=""
            onClick={async () => {
              // rl0211-2 (ROB-B9): `invalidateQueries` resolve mesmo com o refetch falhando; só avisa "recarregado" se nenhum bloco ficou em erro.
              await qc.invalidateQueries({ queryKey: ['pipeline', 'crm', 'relatorios'] });
              const comErro = qc.getQueryCache().findAll({ queryKey: ['pipeline', 'crm', 'relatorios'], type: 'active' }).find((x) => x.state.status === 'error');
              if (comErro) toast.erro(mensagemDeErro(comErro.state.error));
              else toast.sucesso(t('crm.relatorios.recarregado'));
            }}
          >
            {t('crm.relatorios.recarregar')}
          </Botao>
        </div>
      </header>

      <div className="flex min-w-0 flex-wrap items-end gap-2" data-filtros-relatorios="">
        <div className="w-full sm:w-56">
          <Selecao
            aria-label={t('crm.relatorios.funil')}
            icone={<Filter className="size-4" />}
            data-funil=""
            value={funil}
            opcoes={ativos.map((x) => ({ valor: x.id, rotulo: rotuloFunilPipeline(x.nome) }))}
            onChange={(e) => setFunilId(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-60">
          <Selecao
            aria-label={t('crm.relatorios.responsavel')}
            icone={<UserRound className="size-4" />}
            data-responsavel=""
            value={responsavel}
            vazio={t('crm.relatorios.todas')}
            opcoes={opcoesResp}
            onChange={(e) => setResponsavel(e.target.value)}
          />
        </div>
        <fieldset className="flex min-w-0 flex-wrap items-end gap-2" data-periodo="">
          <legend className="sr-only">{t('crm.relatorios.periodo')}</legend>
          <div className="w-40">
            <Rotulo id="rel-de" rotulo={t('crm.relatorios.mesDe')}>
              <CampoData id="rel-de" type="month" data-de="" value={de} aria-invalid={erroPeriodo ? true : undefined} onChange={(e) => setDe(e.target.value)} />
            </Rotulo>
          </div>
          <div className="w-40">
            <Rotulo id="rel-ate" rotulo={t('crm.relatorios.mesAte')}>
              <CampoData id="rel-ate" type="month" data-ate="" value={ate} aria-invalid={erroPeriodo ? true : undefined} onChange={(e) => setAte(e.target.value)} />
            </Rotulo>
          </div>
          {(de || ate) && (
            <Botao
              variante="fantasma"
              data-periodo-padrao=""
              onClick={() => {
                setDe('');
                setAte('');
              }}
            >
              {t('crm.relatorios.periodoPadrao')}
            </Botao>
          )}
        </fieldset>
      </div>
      {erroPeriodo && (
        <p role="alert" className="text-sm text-perigo" data-erro-periodo="">
          {t(erroPeriodo === 'longo' ? 'crm.relatorios.periodoLongo' : 'crm.relatorios.periodoInvalido')}
        </p>
      )}

      {funis.isPending ? (
        <Carregando texto={t('crm.relatorios.carregando')} />
      ) : funis.isError && !funis.data ? (
        <Erro erro={funis.error} aoTentar={() => void funis.refetch()} />
      ) : !funil ? (
        <Vazio texto={t('crm.relatorios.semFunis')} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <BlocoFunil recorte={recorte} />
          <BlocoEvolucao recorte={recorte} periodo={periodo} />
          <BlocoOrigem recorte={recorte} periodo={periodo} />
          <BlocoParados key={`${recorte.funil_id}|${recorte.responsaveis ?? ''}`} recorte={recorte} />
        </div>
      )}
    </div>
  );
}

/** Botão de CSV do bloco (desligado enquanto não há dados). */
function BotaoCsv({ bloco, pronto, aoExportar }: { bloco: Bloco; pronto: boolean; aoExportar: () => void | Promise<void> }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  // R3-SEG-M1: `pipeline.exportar` só vale na tela (nenhuma rota a exige); sem ela o botão não existe, como no Comercial e em Atividades.
  if (!s.pode('pipeline.exportar')) return null;
  const nome = t(`crm.relatorios.blocos.${bloco}`);
  return (
    <button
      type="button"
      className="rounded-lg p-2 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1 disabled:opacity-40"
      aria-label={t('crm.relatorios.exportar', { bloco: nome })}
      title={t('crm.relatorios.exportar', { bloco: nome })}
      disabled={!pronto}
      data-exportar={bloco}
      onClick={() => void aoExportar()}
    >
      <Download className="size-4" aria-hidden />
    </button>
  );
}

/** Carregando, erro (com "tentar de novo") e vazio com texto próprio, por bloco. */
function EstadoBloco<T>({ consulta, vazio, textoVazio, children }: { consulta: { isPending: boolean; isError: boolean; error: unknown; data: T | undefined; refetch: () => unknown }; vazio: (d: T) => boolean; textoVazio: string; children: (d: T) => ReactNode }) {
  const { t } = useTranslation('pipeline');
  if (consulta.isPending) return <Carregando texto={t('crm.relatorios.carregando')} />;
  if (consulta.isError || consulta.data === undefined) return <Erro erro={consulta.error} aoTentar={() => void consulta.refetch()} />;
  if (vazio(consulta.data)) return <Vazio texto={textoVazio} />;
  return <>{children(consulta.data)}</>;
}

function Ajuda({ texto }: { texto: string }) {
  return <p className="mb-3 text-sm text-tinta-3">{texto}</p>;
}

const TH = 'px-3 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-tinta-3 uppercase';
const THN = `${TH} text-right`;
const TDN = 'numero px-3 py-2 text-right whitespace-nowrap';

function Tabela({ rotulo, dado, children }: { rotulo: string; dado: string; children: ReactNode }) {
  return (
    <div className="min-w-0 overflow-x-auto" role="region" aria-label={rotulo} tabIndex={0}>
      <table className="w-full text-sm" data-tabela={dado}>
        {children}
      </table>
    </div>
  );
}

function useExportar(bloco: Bloco, linhas: () => (string | number)[][]) {
  const { t } = useTranslation('pipeline');
  const toast = useToast();
  return () => {
    baixarTexto(t('crm.relatorios.arquivo', { bloco }), csv(linhas()));
    toast.sucesso(t('crm.relatorios.exportado'));
  };
}

// --- Funil de hoje ---------------------------------------------------------------------------------------------------

function BlocoFunil({ recorte }: { recorte: Recorte }) {
  const { t } = useTranslation('pipeline');
  const q = useQuery({ queryKey: ['pipeline', 'crm', 'relatorios', 'funil', recorte], queryFn: () => get<FunilHoje>('/pipeline/crm/relatorios/funil', { ...recorte }), placeholderData: keepPreviousData });
  const exportar = useExportar('funil', () => BLOCOS_CSV_REL.funil(q.data!, t as unknown as Traduzir));
  return (
    <Cartao titulo={t('crm.relatorios.blocos.funil')} acao={<BotaoCsv bloco="funil" pronto={!!q.data && !q.isPlaceholderData} aoExportar={exportar} />}>
      <div aria-busy={q.isFetching} data-bloco="funil">
        <EstadoBloco consulta={q} vazio={(d) => d.etapas.length === 0} textoVazio={t('crm.relatorios.funilHoje.semEtapas')}>
          {(d) => {
            const maximo = Math.max(1, ...d.etapas.map((x) => x.abertas));
            const pausadas = d.etapas.reduce((a, x) => a + x.pausadas, 0);
            return (
              <>
                <Ajuda texto={t('crm.relatorios.funilHoje.ajuda')} />
                <Tabela rotulo={t('crm.relatorios.blocos.funil')} dado="funil">
                  <thead>
                    <tr className="border-b border-borda text-left">
                      <th scope="col" className={TH}>
                        {t('crm.relatorios.funilHoje.etapa')}
                      </th>
                      <th scope="col" className={THN}>
                        {t('crm.relatorios.funilHoje.abertas')}
                      </th>
                      <th scope="col" className={THN}>
                        {t('crm.relatorios.funilHoje.valor')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.etapas.map((x) => (
                      <tr key={x.etapa_id} className="border-t border-borda" data-etapa={x.etapa_id}>
                        <td className="min-w-40 px-3 py-2">
                          <span className="block break-words">{rotuloEtapaPipeline(x.nome)}</span>
                          <span className="mt-1 block h-1.5 rounded bg-sup-3" aria-hidden>
                            <span className="block h-1.5 rounded bg-acento-forte" style={{ width: `${Math.round((x.abertas / maximo) * 100)}%` }} />
                          </span>
                        </td>
                        <td className={TDN}>{numero(x.abertas)}</td>
                        <td className={TDN}>{anunciarOcultos(valoresTexto(x.valores, (v) => v.total_centavos))}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tabela>
                {pausadas > 0 && <p className="mt-2 text-xs text-tinta-3">{t('crm.relatorios.funilHoje.pausadasNota', { count: pausadas, n: numero(pausadas) })}</p>}
              </>
            );
          }}
        </EstadoBloco>
      </div>
    </Cartao>
  );
}

// --- Evolução mês a mês ----------------------------------------------------------------------------------------------

function BlocoEvolucao({ recorte, periodo }: { recorte: Recorte; periodo: { de?: string; ate?: string } }) {
  const { t } = useTranslation('pipeline');
  const params = { ...recorte, ...periodo };
  const q = useQuery({ queryKey: ['pipeline', 'crm', 'relatorios', 'evolucao', params], queryFn: () => get<Evolucao>('/pipeline/crm/relatorios/evolucao', params), placeholderData: keepPreviousData });
  const exportar = useExportar('evolucao', () => BLOCOS_CSV_REL.evolucao(q.data!, t as unknown as Traduzir));
  const cols = ['mes', 'leads', 'vendas', 'perdas', 'taxa', 'recorrente', 'pontual', 'total'] as const;
  return (
    <Cartao titulo={t('crm.relatorios.blocos.evolucao')} acao={<BotaoCsv bloco="evolucao" pronto={!!q.data && !q.isPlaceholderData} aoExportar={exportar} />}>
      <div aria-busy={q.isFetching} data-bloco="evolucao">
        <EstadoBloco consulta={q} vazio={(d) => d.meses.length === 0} textoVazio={t('crm.relatorios.vazio')}>
          {(d) => (
            <>
              <Ajuda texto={t('crm.relatorios.evolucao.ajuda')} />
              <Tabela rotulo={t('crm.relatorios.blocos.evolucao')} dado="evolucao">
                <thead>
                  <tr className="border-b border-borda text-left">
                    {cols.map((c) => (
                      <th key={c} scope="col" className={c === 'mes' ? TH : THN}>
                        {t(`crm.relatorios.evolucao.${c}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.meses.map((m) => (
                    <tr key={m.mes} className="border-t border-borda" data-mes={m.mes}>
                      <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        {mesAno(m.mes)}
                      </th>
                      <td className={TDN}>{numero(m.leads)}</td>
                      <td className={TDN}>{numero(m.vendas)}</td>
                      <td className={TDN}>{numero(m.perdas)}</td>
                      <td className={TDN} data-taxa="">
                        {taxaTexto(m.taxa)}
                      </td>
                      <td className={TDN}>{anunciarOcultos(valoresTexto(m.valores, (v) => v.recorrente_centavos))}</td>
                      <td className={TDN}>{anunciarOcultos(valoresTexto(m.valores, (v) => v.unico_centavos))}</td>
                      <td className={TDN}>{anunciarOcultos(valoresTexto(m.valores, (v) => v.total_centavos))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-borda font-semibold" data-total-periodo="">
                    <th scope="row" className="px-3 py-2 text-left whitespace-nowrap">
                      {t('crm.relatorios.evolucao.totalPeriodo')}
                    </th>
                    <td className={TDN}>{numero(d.total.leads)}</td>
                    <td className={TDN}>{numero(d.total.vendas)}</td>
                    <td className={TDN}>{numero(d.total.perdas)}</td>
                    <td className={TDN}>{taxaTexto(d.total.taxa)}</td>
                    <td className={TDN}>{anunciarOcultos(valoresTexto(d.total.valores, (v) => v.recorrente_centavos))}</td>
                    <td className={TDN}>{anunciarOcultos(valoresTexto(d.total.valores, (v) => v.unico_centavos))}</td>
                    <td className={TDN}>{anunciarOcultos(valoresTexto(d.total.valores, (v) => v.total_centavos))}</td>
                  </tr>
                </tfoot>
              </Tabela>
            </>
          )}
        </EstadoBloco>
      </div>
    </Cartao>
  );
}

// --- Origem ----------------------------------------------------------------------------------------------------------

function BlocoOrigem({ recorte, periodo }: { recorte: Recorte; periodo: { de?: string; ate?: string } }) {
  const { t } = useTranslation('pipeline');
  const params = { ...recorte, ...periodo };
  const q = useQuery({ queryKey: ['pipeline', 'crm', 'relatorios', 'origem', params], queryFn: () => get<Origem>('/pipeline/crm/relatorios/origem', params), placeholderData: keepPreviousData });
  const exportar = useExportar('origem', () => BLOCOS_CSV_REL.origem(q.data!, t as unknown as Traduzir));
  return (
    <Cartao titulo={t('crm.relatorios.blocos.origem')} acao={<BotaoCsv bloco="origem" pronto={!!q.data && !q.isPlaceholderData} aoExportar={exportar} />}>
      <div aria-busy={q.isFetching} data-bloco="origem">
        <EstadoBloco consulta={q} vazio={(d) => d.fontes.length === 0} textoVazio={t('crm.relatorios.vazio')}>
          {(d) => (
            <>
              <Ajuda texto={t('crm.relatorios.origem.ajuda')} />
              <Tabela rotulo={t('crm.relatorios.blocos.origem')} dado="origem">
                <thead>
                  <tr className="border-b border-borda text-left">
                    <th scope="col" className={TH}>
                      {t('crm.relatorios.origem.fonte')}
                    </th>
                    <th scope="col" className={THN}>
                      {t('crm.relatorios.origem.leads')}
                    </th>
                    <th scope="col" className={THN}>
                      {t('crm.relatorios.origem.vendas')}
                    </th>
                    <th scope="col" className={THN}>
                      {t('crm.relatorios.origem.valor')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.fontes.map((x, i) => (
                    // A Fonte é texto livre (some no modo discreto): o gancho da linha é o índice, nunca o valor (rl0211-1 SEG-B3).
                    <tr key={x.fonte ?? '__sem_fonte'} className="border-t border-borda" data-fonte-linha={i} data-sem-fonte={x.fonte ? undefined : ''}>
                      <td className="max-w-64 min-w-32 px-3 py-2 break-words">{x.fonte ? textoLivre(x.fonte) : t('crm.relatorios.origem.semFonte')}</td>
                      <td className={TDN}>{numero(x.leads)}</td>
                      <td className={TDN}>{numero(x.vendas)}</td>
                      <td className={TDN}>{anunciarOcultos(valoresTexto(x.valores, (v) => v.total_centavos))}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
              {d.truncado && (
                <p className="mt-2 text-xs text-tinta-3" role="status" data-truncado="">
                  {t('crm.relatorios.origem.truncado', { n: numero(d.fontes.length) })}
                </p>
              )}
            </>
          )}
        </EstadoBloco>
      </div>
    </Cartao>
  );
}

// --- Negócios parados ------------------------------------------------------------------------------------------------

function BlocoParados({ recorte }: { recorte: Recorte }) {
  const { t } = useTranslation('pipeline');
  const toast = useToast();
  const [pagina, setPagina] = useState(1);
  const [motivo, setMotivo] = useState<MotivoParado | ''>('');
  const params = { ...recorte, ...(motivo ? { motivo } : {}), pagina, limite: LIMITE_PAGINA };
  const q = useQuery({ queryKey: ['pipeline', 'crm', 'relatorios', 'parados', params], queryFn: () => get<Parados>('/pipeline/crm/relatorios/parados', params), placeholderData: keepPreviousData });
  const paginas = Math.max(1, Math.ceil((q.data?.total ?? 0) / LIMITE_PAGINA));
  // rl0211-1 ROB-B1: a lista encolheu (negociação resolvida, recarregou) e a página pedida ficou além do fim: volta para a última.
  useEffect(() => {
    if (q.data && !q.isPlaceholderData && q.data.total > 0 && pagina > paginas) setPagina(paginas);
  }, [q.data, q.isPlaceholderData, pagina, paginas]);
  // O CSV leva a lista toda (páginas de 200) até o teto; passou do teto, avisa.
  const exportar = async () => {
    try {
      const todos: Parado[] = [];
      let total = 0;
      for (let p = 1; todos.length < LIMITE_CSV; p++) {
        const r = await get<Parados>('/pipeline/crm/relatorios/parados', { ...recorte, ...(motivo ? { motivo } : {}), pagina: p, limite: 200 });
        total = r.total;
        todos.push(...r.itens);
        if (r.itens.length < 200 || todos.length >= r.total) break;
      }
      baixarTexto(t('crm.relatorios.arquivo', { bloco: 'parados' }), csv(BLOCOS_CSV_REL.parados(todos.slice(0, LIMITE_CSV), t as unknown as Traduzir)));
      if (total > LIMITE_CSV) toast.info(t('crm.relatorios.parados.truncadoCsv', { n: numero(LIMITE_CSV) }));
      else toast.sucesso(t('crm.relatorios.exportado'));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  return (
    <Cartao titulo={t('crm.relatorios.blocos.parados')} acao={<BotaoCsv bloco="parados" pronto={!!q.data && !q.isPlaceholderData && q.data.total > 0} aoExportar={exportar} />}>
      <div aria-busy={q.isFetching} data-bloco="parados">
        <Ajuda texto={t('crm.relatorios.parados.ajuda')} />
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-64">
            <Selecao
              aria-label={t('crm.relatorios.parados.filtroMotivo')}
              data-motivo=""
              value={motivo}
              vazio={t('crm.relatorios.parados.todosMotivos')}
              opcoes={MOTIVOS_PARADO.map((m) => ({ valor: m, rotulo: t(`crm.relatorios.parados.motivos.${m}`) }))}
              onChange={(e) => {
                setMotivo(e.target.value as MotivoParado | '');
                setPagina(1);
              }}
            />
          </div>
          {q.data && (
            <p className="text-sm font-medium text-tinta-2" role="status" data-total-parados="">
              {t('crm.relatorios.parados.total', { count: q.data.total, n: numero(q.data.total) })}
            </p>
          )}
        </div>
        <EstadoBloco consulta={q} vazio={(d) => d.total === 0} textoVazio={t('crm.relatorios.parados.nenhum')}>
          {(d) => (
            <>
              <Tabela rotulo={t('crm.relatorios.blocos.parados')} dado="parados">
                <thead>
                  <tr className="border-b border-borda text-left">
                    {(['negociacao', 'etapa', 'responsavel', 'valor', 'motivo', 'ultimoMovimento'] as const).map((c) => (
                      <th key={c} scope="col" className={c === 'valor' ? THN : TH}>
                        {t(`crm.relatorios.parados.${c}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.itens.map((x) => (
                    <tr key={x.id} className="border-t border-borda align-top" data-parado={x.id}>
                      <td className="max-w-72 min-w-40 px-3 py-2">
                        <Link className="font-medium break-words text-acento-forte hover:underline" to={caminhoDaNegociacao(x.id)}>
                          {textoLivre(x.titulo, x.parceiro_nome)}
                        </Link>
                      </td>
                      <td className="min-w-28 px-3 py-2 break-words">{rotuloEtapaPipeline(x.etapa_nome)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{x.responsavel_nome ? nomePessoa(x.responsavel_nome) : ''}</td>
                      <td className={TDN}>{anunciarOcultos(dinheiro(x.valor_centavos, x.moeda))}</td>
                      <td className="px-3 py-2">
                        <span className="flex flex-wrap gap-1" data-motivos="">
                          {x.motivos.map((m) => (
                            <Selo key={m} cor={m === 'tarefa_vencida' ? 'perigo' : m === 'sem_proxima_tarefa' ? 'dourado' : 'neutro'}>
                              {t(`crm.relatorios.parados.motivos.${m}`)}
                            </Selo>
                          ))}
                        </span>
                        {x.tarefa_vencida_em && <span className="mt-1 block text-xs text-tinta-3">{t('crm.relatorios.parados.tarefaVencidaEm', { data: data(x.tarefa_vencida_em) })}</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="block">{dataHora(x.ultimo_movimento)}</span>
                        <span className="block text-xs text-tinta-3">{x.dias_sem_movimento < 1 ? t('crm.relatorios.parados.hoje') : t('crm.relatorios.parados.dias', { count: x.dias_sem_movimento })}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
              {paginas > 1 && (
                <nav className="mt-3 flex flex-wrap items-center justify-between gap-2" aria-label={t('crm.relatorios.blocos.parados')} data-paginacao="">
                  <Botao tamanho="sm" icone={<ChevronLeft className="size-4" />} disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} aria-label={t('crm.relatorios.parados.anterior')} data-anterior="" />
                  <span className="text-sm text-tinta-2">{t('crm.relatorios.parados.pagina', { pagina: numero(pagina), paginas: numero(paginas) })}</span>
                  <Botao tamanho="sm" icone={<ChevronRight className="size-4" />} disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)} aria-label={t('crm.relatorios.parados.proxima')} data-proxima="" />
                </nav>
              )}
            </>
          )}
        </EstadoBloco>
      </div>
    </Cartao>
  );
}
